import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { apiGetById } from "../../services/genesisApi/httpsMethods";
import type { Borrower, Sponsor } from "../../types/deal";
import { InfoSection } from "../../components/common/infoSection";
import "../../assets/css/Text-General.css";

interface SponsorDetailProps {
  sponsorId: number;
  isEdit: boolean;
  /** Increment to trigger a refetch (e.g. after AI research updates the sponsor). */
  refreshTrigger?: number;
  /** Deal-level borrower rows when embedding from a deal (may be partial vs full `Borrower`). */
  borrowers?: Partial<Borrower>[];
}

const renderInlineMarkdown = (
  segment: string,
  _keyPrefix: string,
): ReactNode[] => {
  const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
  const boldRegex = /\*\*(.+?)\*\*/g;

  const renderBold = (text: string, boldKeyPrefix: string): ReactNode[] => {
    const nodes: ReactNode[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    let boldIndex = 0;

    while ((match = boldRegex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        nodes.push(text.slice(lastIndex, match.index));
      }
      nodes.push(
        <strong key={`${boldKeyPrefix}-b-${boldIndex++}`}>{match[1]}</strong>,
      );
      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < text.length) {
      nodes.push(text.slice(lastIndex));
    }

    return nodes;
  };

  const result: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let linkIndex = 0;

  while ((match = linkRegex.exec(segment)) !== null) {
    if (match.index > lastIndex) {
      result.push(
        ...renderBold(segment.slice(lastIndex, match.index), `t-${linkIndex}`),
      );
    }

    const [, label, href] = match;
    result.push(
      <a
        key={`link-${linkIndex++}`}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: "#1B7A63", textDecoration: "underline" }}
      >
        {renderBold(label, `l-${linkIndex}`)}
      </a>,
    );

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < segment.length) {
    result.push(...renderBold(segment.slice(lastIndex), `t-end`));
  }

  return result;
};

const renderSimpleMarkdown = (text: string): ReactNode[] => {
  const lines = text.split(/\r?\n/);
  const nodes: ReactNode[] = [];

  lines.forEach((rawLine, index) => {
    const line = rawLine.trimEnd();
    if (!line) {
      // Preserve blank lines as spacing
      nodes.push(<br key={`br-${index}`} />);
      return;
    }

    const isHeader = line.trimStart().startsWith("## ");
    const content = isHeader ? line.trimStart().slice(3) : line;
    const inline = renderInlineMarkdown(content, `line-${index}`);

    if (isHeader) {
      nodes.push(
        <div
          key={`h-${index}`}
          style={{
            marginTop: index === 0 ? 0 : 16,
            marginBottom: 4,
            fontWeight: 600,
            color: "#000",
            textDecoration: "underline",
            textUnderlineOffset: 3,
          }}
        >
          {inline}
        </div>,
      );
    } else {
      nodes.push(<span key={`p-${index}`}>{inline}</span>);
    }

    if (index < lines.length - 1 && !isHeader) {
      nodes.push("\n");
    }
  });

  return nodes;
};

const SponsorDetail = ({
  sponsorId,
  isEdit,
  refreshTrigger,
  borrowers,
}: SponsorDetailProps) => {
  const [sponsor, setSponsor] = useState<Sponsor | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!sponsorId) return;
    apiGetById("sponsor", sponsorId)
      .then((response) => {
        if (response && response.success && response.data) {
          setSponsor(response.data[0]);
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching sponsor", err);
        setLoading(false);
      });
  }, [sponsorId, refreshTrigger]);

  if (loading) return <CircularProgress />;
  if (!sponsor) return <Typography>No Sponsor found</Typography>;

  const borrowersForDisplay = borrowers;

  return (
    <Box sx={{ display: "flex", gap: "10px", flexDirection: "column" }}>
      <Box sx={{ display: "flex", flexDirection: "row" }}>
        {isEdit ? (
          <Box sx={{ width: isEdit ? "75%" : "100%", display: "flex", gap: 1 }}>
            <Typography
              className="text-general-page-header-prefix"
              sx={{ mb: "0 !important", paddingBottom: "0 !important" }}
            >
              {"SPONSOR:"}
            </Typography>
            <Typography
              className="text-general-page-header"
              sx={{ mb: "0 !important", paddingBottom: 0 }}
            >
              {sponsor.sponsor_name}
            </Typography>
          </Box>
        ) : null}
      </Box>
      <Box
        sx={{ display: "flex", flexDirection: "row", gap: 2, width: "100%" }}
      >
        <InfoSection
          title="Overview"
          items={[
            {
              label: "Sponsor",
              value: sponsor.sponsor_name,
              link: `/sponsors/${sponsor.id}`,
            },
            {
              label: "Borrowers",
              value: borrowersForDisplay
                ?.map((borrower) => borrower.name)
                .filter(Boolean)
                .join(", "),
            },
            { label: "Sponsor Address", value: sponsor.sponsor_address },
            { label: "Sponsor Type", value: sponsor.sponsor_type },
            {
              label: "KYC Completed",
              value: sponsor.kyc_completed ? "Yes" : "No",
            },
            { label: "Short Description", value: sponsor.short_description },
            { label: "Credit Risk", value: sponsor.credit_risk },
            { label: "No. of Deals", value: sponsor.deal_count },
            { label: "No. of Live Deals", value: sponsor.live_deal_count },
            { label: "No. of Loans Issued", value: sponsor.issued_loan_count },
          ]}
        />
        {(sponsor.credit_assessment ?? sponsor.long_description) && (
          <Box
            sx={{
              minWidth: 0,
              backgroundColor: "#ffffff",
              p: 3,
              borderRadius: "12px",
              border: "1px solid #e5e7eb",
              boxShadow:
                "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)",
              mb: 1,
              width: "50%",
            }}
          >
            <Typography
              component="h3"
              sx={{
                m: 0,
                mb: 3,
                fontSize: "1rem !important",
                fontWeight: "500 !important",
                color: "#111827",
                lineHeight: 1.2,
                letterSpacing: "-0.01em",
              }}
            >
              Credit Assessment
            </Typography>
            <Typography
              component="div"
              sx={{
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                typography: "body2",
                color: "text.secondary",
                "& strong": { fontWeight: 600, color: "text.primary" },
                "& a": { color: "#1B7A63", textDecoration: "underline" },
              }}
            >
              {renderSimpleMarkdown(
                sponsor.credit_assessment ?? sponsor.long_description ?? "",
              )}
            </Typography>
          </Box>
        )}
      </Box>

      <Box
        sx={{ display: "flex", flexDirection: "row", gap: 2, width: "100%" }}
      ></Box>
      {/*
    <Typography className="text-general-page-text">coming soon...</Typography>
    <Typography className="text-general-page-subheader">Contacts</Typography>
    <Typography className="text-general-page-text">coming soon...</Typography> */}
    </Box>
  );
};

export default SponsorDetail;
